import {
  UploadItem,
  UploadItemStatus,
} from "@/components/upload/types/upload-item";
import type {
  IndexingStatusPayload,
  UploadStatusSnapshotEntry,
} from "../types/upload.types";

/**
 * How one SendIndexingStatusMessage changes an upload item. Pure, so the
 * choreography — in particular the LAR-412 "Indexing" hand-off, where the
 * upload completes while indexing keeps running in the background — is
 * testable without a hub connection.
 */
export function applyIndexingStatusMessage(
  upload: UploadItem,
  payload: IndexingStatusPayload
): UploadItem {
  const next: UploadItem = {
    ...upload,
    hasFeedback: !!payload.feedback,
    feedback: payload.feedback,
  };

  switch (payload.status) {
    case "Processing":
      return { ...next, status: UploadItemStatus.Processing };
    case "Indexing":
      // Accepted as a background run: the document row's spinner takes over
      // once the workspace refetches, so this is progress, not an error.
      return {
        ...next,
        status: UploadItemStatus.Indexing,
        indexingInBackground: true,
        hasIndexingError: false,
      };
    case "Indexed":
      return {
        ...next,
        status: UploadItemStatus.Indexed,
        indexingInBackground: false,
        hasIndexingError: false,
      };
    case "IndexingFailed":
      return {
        ...next,
        status: UploadItemStatus.IndexingFailed,
        indexingInBackground: false,
        hasIndexingError: true,
      };
    case "Failed":
      return {
        ...next,
        status: UploadItemStatus.Failed,
        indexingInBackground: false,
        hasIndexingError: false,
      };
    case "Completed": {
      // The document is in the workspace. Whether indexing is done, failed
      // or still running was established by the message before this one;
      // the payload's own flag wins when the API sends it.
      const indexingInBackground =
        payload.indexingInBackground ?? upload.indexingInBackground ?? false;
      return {
        ...next,
        status: UploadItemStatus.Completed,
        indexingInBackground,
        hasIndexingError: indexingInBackground
          ? false
          : (upload.hasIndexingError ?? false),
      };
    }
    default:
      return next;
  }
}

/**
 * How one reconnect-snapshot entry changes an upload item. The snapshot is
 * derived from the stored revision and the worker's operation row, so it
 * has no "Processing"/"Indexed" phases — only outcomes.
 */
export function applyUploadStatusSnapshot(
  upload: UploadItem,
  entry: UploadStatusSnapshotEntry
): UploadItem {
  switch (entry.status) {
    case "Completed":
      return {
        ...upload,
        status: UploadItemStatus.Completed,
        indexingInBackground: false,
        hasIndexingError: false,
      };
    case "Indexing":
      return {
        ...upload,
        status: UploadItemStatus.Completed,
        indexingInBackground: true,
        hasIndexingError: false,
      };
    case "IndexingFailed":
      return {
        ...upload,
        status: UploadItemStatus.Completed,
        indexingInBackground: false,
        hasIndexingError: true,
      };
    case "Failed":
      return {
        ...upload,
        status: UploadItemStatus.Failed,
        indexingInBackground: false,
        hasIndexingError: false,
      };
    case "Unknown":
      // Not in the workspace yet — still on its way through the pipeline.
      return upload.status === UploadItemStatus.Pending
        ? { ...upload, status: UploadItemStatus.Processing }
        : upload;
    default:
      return upload;
  }
}
